import React, { useContext } from "react";
import { Box, Grid } from "@mui/material";
import UserContext from "../../services/user/user";
import {
  ECAxiosAPI,
  EcAuthContext,
  EcUserDataContext
} from "@eclear-ag/ec-common-ui";
import { useForm } from "react-hook-form";
import { URLS } from "../../configuration/urls";
import {
  BaseButton,
  BaseFormCard,
  BaseTextField as FormTextField,
  BaseDropdown as FormDropDown
} from "../base";
import { FormattedMessage } from "react-intl";
import Typography from "../base/typography";

const countriesList_en = [
  {
    name: "Austria",
    value: "AT"
  },
  {
    name: "Belgium",
    value: "BE"
  },
  {
    name: "Bulgaria",
    value: "BG"
  },
  {
    name: "China",
    value: "CN"
  },
  {
    name: "Croatia",
    value: "HR"
  },
  {
    name: "Cyprus",
    value: "CY"
  },
  {
    name: "Czech Republic",
    value: "CZ"
  },
  {
    name: "Denmark",
    value: "DK"
  },
  {
    name: "Estonia",
    value: "EE"
  },
  {
    name: "Finland",
    value: "FI"
  },
  {
    name: "France",
    value: "FR"
  },
  {
    name: "Germany",
    value: "DE"
  },
  {
    name: "Greece",
    value: "EL"
  },
  {
    name: "Hungary",
    value: "HU"
  },
  {
    name: "Ireland",
    value: "IE"
  },
  {
    name: "Italy",
    value: "IT"
  },
  {
    name: "Latvia",
    value: "LV"
  },
  {
    name: "Lithuania",
    value: "LT"
  },
  {
    name: "Luxembourg",
    value: "LU"
  },
  {
    name: "Malta",
    value: "MT"
  },
  {
    name: "Netherlands",
    value: "NL"
  },
  // {
  //   name: "Northern Ireland",
  //   value: "NIR",
  // },
  {
    name: "Poland",
    value: "PL"
  },
  {
    name: "Portugal",
    value: "PT"
  },
  {
    name: "Romania",
    value: "RO"
  },
  {
    name: "Switzerland",
    value: "CH"
  },
  {
    name: "Slovakia",
    value: "SK"
  },
  {
    name: "Slovenia",
    value: "SI"
  },
  {
    name: "Spain",
    value: "ES"
  },
  {
    name: "Sweden",
    value: "SE"
  },
  {
    name: "United Kingdom",
    value: "GB"
  }
];

const countriesList_de = [
  {
    name: "Belgien",
    value: "BE"
  },
  {
    name: "Bulgarien",
    value: "BG"
  },
  {
    name: "China",
    value: "CN"
  },
  {
    name: "Dänemark",
    value: "DK"
  },
  {
    name: "Deutschland",
    value: "DE"
  },
  {
    name: "Estland",
    value: "EE"
  },
  {
    name: "Finnland",
    value: "FI"
  },
  {
    name: "Frankreich",
    value: "FR"
  },
  {
    name: "Griechenland",
    value: "EL"
  },
  {
    name: "Irland",
    value: "IE"
  },
  {
    name: "Italien",
    value: "IT"
  },
  {
    name: "Kroatien",
    value: "HR"
  },
  {
    name: "Lettland",
    value: "LV"
  },
  {
    name: "Litauen",
    value: "LT"
  },
  {
    name: "Luxemburg",
    value: "LU"
  },
  {
    name: "Malta",
    value: "MT"
  },
  {
    name: "Niederlande",
    value: "NL"
  },
  // {
  //   name: "Nordirland",
  //   value: "NIR",
  // },
  {
    name: "Österreich",
    value: "AT"
  },
  {
    name: "Polen",
    value: "PL"
  },
  {
    name: "Portugal",
    value: "PT"
  },
  {
    name: "Rumänien",
    value: "RO"
  },
  {
    name: "Schweden",
    value: "SE"
  },
  {
    name: "Schweiz",
    value: "CH"
  },
  {
    name: "Slowakei",
    value: "SK"
  },
  {
    name: "Slowenien",
    value: "SI"
  },
  {
    name: "Spanien",
    value: "ES"
  },
  {
    name: "Tschechien",
    value: "CZ"
  },
  {
    name: "Ungarn",
    value: "HU"
  },
  {
    name: "Vereinigtes Königreich",
    value: "GB"
  },
  {
    name: "Zypern",
    value: "CY"
  }
];

const CompanyInfo = ({ showSave }) => {
  const { lang } = useContext(UserContext);

  const { user } = useContext(EcAuthContext);
  const { userData } = useContext(EcUserDataContext);
  const { setIsSaveSuccess, setIsSnackBarVisible } = useContext(UserContext);

  const [isLoading, setIsLoading] = React.useState(true);

  const [isSaving, setIsSaving] = React.useState(false);

  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    reset,
    getValues
  } = useForm({
    mode: "onChange",
    defaultValues: {
      vatId: "",
      companyName: "",
      channelPartner: "",
      legalForm: "",
      street: "",
      city: "",
      postalCode: "",
      country: "",
      houseNumber: "",
      phoneNumber: ""
    }
  });

  const vatId = watch("vatId");

  React.useEffect(() => {
    const loadCompanyData = async () => {
      setIsLoading(false);
      const res = userData.companyDetails[0];

      reset({
        channelPartner: res.channelPartner,
        city: res.city,
        country: res.country,
        houseNumber: res.houseNumber,
        legalForm: res.legalForm,
        companyName: res.name,
        phoneNumber: res.phoneNumber,
        postalCode: res.postalCode,
        street: res.street,
        vatId: res.vatId
      });
    };

    if (userData.companyId) {
      loadCompanyData();
    }
  }, [userData]);

  const onSubmit = async () => {
    const payload = {
      ...getValues(),
      name: getValues("companyName"),
      merchantId: userData.companyId
    };
    setIsSaving(true);
    await ECAxiosAPI.put(
      URLS.UPDATE_COMPANY_INFO(userData.companyId),
      payload,
      {
        userId: userData.userInfo.id
      }
    )
      .then((res) => {
        setIsSaveSuccess(true);
      })
      .catch((err) => {
        console.log("Error updating user info: ", err);
        setIsSaveSuccess(false);
      });
    setIsSaving(false);
    setIsSnackBarVisible(true);
  };

  return (
    <>
      <BaseFormCard
        title="companyInformation"
        onSubmit={handleSubmit(onSubmit)}
        footerJustify="flex-end"
        footer={
          showSave ? (
            <BaseButton
              type="submit"
              variant="filled"
              disabled={isSaving}
              loading={isSaving}
              data-testid="save-button"
            >
              <FormattedMessage id="save" />
            </BaseButton>
          ) : undefined
        }
      >
        <Box
          sx={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            gap: "16px"
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormTextField
                width={12}
                errors={errors}
                control={control}
                label={"vatId"}
                type="vat"
                value={vatId}
                disabled
                inputProps={{
                  "data-testid": "vatid-input"
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"companyName"}
                required
                disabled
                inputProps={{
                  "data-testid": "company-name-input"
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"legalForm"}
                required
                disabled
                inputProps={{
                  "data-testid": "legal-form-input"
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"street"}
                disabled
                required
                inputProps={{
                  "data-testid": "street-input"
                }}
              />{" "}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"houseNumber"}
                disabled
                inputProps={{
                  "data-testid": "house-number-input"
                }}
              />{" "}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"postalCode"}
                required
                disabled
                validation={{
                  matchPattern: (value) => /^[a-zA-Z0-9]{5,}$/.test(value)
                }}
                validationMessage={<FormattedMessage id="invalid postalcode" />}
                inputProps={{
                  "data-testid": "postal-code-input"
                }}
              />{" "}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"city"}
                required
                disabled
                inputProps={{
                  "data-testid": "city-input"
                }}
              />{" "}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormTextField
                width={6}
                errors={errors}
                control={control}
                label={"phoneNumber"}
                disabled
                inputProps={{
                  "data-testid": "phone-number-input"
                }}
              />{" "}
            </Grid>
            <Grid item xs={12} md={6}>
              <FormDropDown
                control={control}
                width={6}
                placeholder={<FormattedMessage id="country_placeholder" />}
                label={"country"}
                errors={errors}
                disabled
                options={
                  userData.lang === "en" ? countriesList_en : countriesList_de
                }
                required
                inputProps={{
                  "data-testid": "country-input"
                }}
              />{" "}
            </Grid>
          </Grid>
        </Box>
        </BaseFormCard>
      <Box sx={{ mt: "20px" }}>
        <Typography
          styles={{
            width: "676px",
            maxWidth: "100%",
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: "normal",
            fontStretch: "normal",
            fontStyle: "normal",
            lineHeight: 1.5,
            letterSpacing: "normal",
            textAlign: "left",
            color: "var(--font-strong-default)"
          }}
        >
          <FormattedMessage id="Changes to company details are not allowed" />.
          <br />
          <FormattedMessage id="Please contact the support team" />{" "}
          <a
            href="mailto:support@eclear.com"
            style={{ color: "var(--font-strong-default)" }}
          >
            support@eclear.com
          </a>{" "}
          <FormattedMessage id="if you want to apply any changes" />.
        </Typography>
      </Box>
    </>
  );
};

export default CompanyInfo;
